import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AppsState } from '../../../../../core/state/apps.state';
import { BehaviorSubject, forkJoin, Subscription } from 'rxjs';
import { App, AppId } from '../../../../../shared/models/app';
import { User, UserId } from '../../../../../shared/models/user';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../app/app.state';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '../../../../../core/services/session.service';
import { AppsService } from '../../../../../core/services/apps.service';
import { UrlService } from '../../../../../core/services/url.service';
import { UsersService } from '../../../../../core/services/users.service';
import { ShadeToolsService } from '../../../../../core/services/shade-tools.service';
import { TitleBreadcrumbsChangedAction } from '../../../../../core/actions/title.actions';
import { SortTable } from '../../../../../shared/models/sort-table/types';
import { SortTableCellEmpty, SortTableCellKebabMenu, SortTableCellTextPill } from '../../../../../shared/models/sort-table/cell-classes';
import { SortTableComponent } from '../../../../components/sort-table/sort-table.component';
import { ObjectType } from 'src/shared/models/object.model';
import { PermissionsService } from 'src/core/services/permissions.service';
import { Role } from 'src/shared/models/fga/role';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';

@Component({
  selector: 'motn-manage-staff-prop-manager',
  templateUrl: './manage-staff-prop-manager.component.html',
  styleUrls: ['./manage-staff-prop-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SortTableComponent,
  ],
})
export class ManageStaffPropManagerComponent implements OnInit, OnDestroy {
  app: App;
  appId: AppId;
  appUsers: User[];

  childApps: App[];

  private appsSub: Subscription;

  table$ = new BehaviorSubject<SortTable>(null);


  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private session: SessionService,
    private appsService: AppsService,
    private usersService: UsersService,
    private permissionsService: PermissionsService,
    private shadeToolsService: ShadeToolsService,
  ) {
  }


  ngOnInit(): void {
    this.appId = this.appsService.currentAppId;
    this.appsService.fetchApp(this.appId);

    this.appsSub = this.store.select('apps').subscribe(
      (data: AppsState) => {
        this.app = data.apps.find((a) => {
          return a.ID === this.appId;
        });

        if (this.app) {
          this.updateTitle();

          if (!this.appUsers) {
            this.appUsers = [];
            this.updateAppUsers();
          }
        }
      },
    );
  }


  ngOnDestroy() {
    this.appsSub?.unsubscribe();
    this.table$?.complete();
  }


  private updateAppUsers() {
    this.usersService.findUsers$('AppID', '' + this.appId).subscribe(
      (users) => {
        this.appUsers = users;
        this.updateTable();
      },
    );
  }


  addUser() {
    const dialogButtons = this.createAddUserDialogButtons();

    this.shadeToolsService.startDialog(
      'Add Staff User',
      'Enter the Motion username of the user you wish to add as a manager of all units at this property.',
      dialogButtons,
      true,
      false,
      false,
      false
    );
  }

  private createAddUserDialogButtons() {
    return [
      {
        text: 'Add',
        callback: (input) => this.handleAddUserRequest(input.trim()),
      },
      {
        text: 'Cancel',
        callback: () => this.shadeToolsService.closeAll(),
        color: DialogBoxButtonColor.Alt,
      },
    ];
  }

  private handleAddUserRequest(username: string): void {
    this.usersService.findUser$('Username', username).subscribe({
      next: (user) => this.assignRolesToUserForApps(user),
      error: () => this.shadeToolsService.errorDialog('No user with that username could be found.')
    });
  }

  //TODO: This is adding removing stuff user in the property manager experience. should be ADMIN role for a
  private assignRolesToUserForApps(user: User): void {
    this.permissionsService.assignRolesToUser({
      UserID: user.ID,
      ObjectID: this.app.ID,
      ObjectType: ObjectType.APP,
      Roles: [Role.ADMIN],
    }).subscribe({
      next: () => {
        this.updateAppUsers();
        this.shadeToolsService.closeAll();
      },
      error: () => this.shadeToolsService.errorDialog()
    });
  }


  private removeUser(userId: UserId): void {
    const user = this.appUsers.find(u => u.ID === userId);
    const dialogButtons = this.createRemoveUserDialogButtons(userId);

    this.shadeToolsService.startDialog(
      'Remove Staff User',
      `Are you sure you want to remove ${user.Name}?`,
      dialogButtons,
      false,
      false,
      false,
      false
    );
  }

  private createRemoveUserDialogButtons(userId: UserId) {
    return [
      {
        text: 'Remove',
        callback: () => this.handleRemoveUserConfirm(userId),
        color: DialogBoxButtonColor.Red,
      },
      {
        text: 'Cancel',
        callback: () => this.shadeToolsService.closeAll(),
        color: DialogBoxButtonColor.Alt,
      },
    ];
  }

  private handleRemoveUserConfirm(userId: UserId): void {
    this.permissionsService.setUserRoles({
      UserID: userId,
      ObjectID: this.app.ID,
      ObjectType: ObjectType.APP,
      Roles: [],
    }).subscribe({
      next: () => {
        this.updateAppUsers();
        this.shadeToolsService.closeAll();
      },
      error: () => this.shadeToolsService.errorDialog()
    });
  }


  private updateTable() {
    if (!this.appUsers) {
      return;
    }

    const table: SortTable = {
      css: {
        'min-width': '500px',
      },
      columns: [
        {
          name: 'Name',
          css: {
            'width': 'calc(50% - 25px)',
          },
          sortingFn: SortTableCellTextPill.sortingFnMainText,
        },
        {
          name: 'Motion Username',
          css: {
            'width': 'calc(50% - 25px)',
          },
          sortingFn: SortTableCellTextPill.sortingFnMainText,
        },
        {
          name: '',
          css: {
            'width': '50px',
            'justify-content': 'right',
          },
        },
      ],
      rows: [],
    };

    for (let user of this.appUsers) {
      let ownerUser = this.appUsers.find(
        (u) => {
          return u.ID === this.app.Owner;
        },
      );

      let userIsOwner = ownerUser && user.ID === ownerUser.ID;

      table.rows.push({
        context: '' + user.ID,
        cells: [
          new SortTableCellTextPill(user.Name, ''),
          new SortTableCellTextPill(user.Username, userIsOwner ? 'App Owner' : 'Staff'),
          userIsOwner ? new SortTableCellEmpty() : new SortTableCellKebabMenu(user.ID, [
            {
              id: '0',
              name: 'Remove User',
              callback: () => {
                this.removeUser(user.ID);
              },
            },
          ], '', false, 1),
        ],
      });
    }

    this.table$.next(table);
  }


  private updateTitle() {
    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [{
        path: UrlService.makePropManagerManageUnitsUrl(this.appId),
        text: 'Property Manager',
      }, {
        // path: this.urlService.makePropManagerManageUnitsUrl(this.appId),
        text: 'Manage Staff',
      }],
    ));
  }
}
